import { useCallback, useEffect, useState } from "react";
import { Zap, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

  const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("en-US") : "—");
const fmtDay = (iso) => {
  if (!iso) return "—";
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
  const M = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];
  return `${d} ${M[m - 1]} ${y}`;
};

const Field = ({ label, value }) => (
  <div className="rounded-xl bg-white/60 px-3 py-2.5">
    <p className="text-[11px] text-[#8E8E93]">{label}</p>
    <p className="mt-0.5 text-[13.5px] font-medium text-[#1D1D1F]">{value}</p>
  </div>
);

export default function FingerspotAutoSync() {
  const { readOnly } = useAuth();
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { setS((await api.get("/fingerspot/auto-sync-setting")).data); } catch { /* silent */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/fingerspot/auto-sync-setting", { enabled: !s.enabled });
      setS(data);
      toast.success(data.enabled ? "Automatic sync enabled" : "Automatic sync disabled");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  if (!s) return null;
  return (
    <section data-testid="fp-auto-sync" className="liquid-glass-card space-y-4 p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-[15px] font-semibold tracking-tight">
            <Zap className="h-4.5 w-4.5 text-[#128C3E]" /> Payroll Sync Automation
          </h2>
          <p className="mt-0.5 text-[12.5px] text-[#8E8E93]">New payroll periods (from {fmtDay(s.effective_from)}) automatically enable Fingerspot Attendance Sync — no monthly Activate click needed.</p>
        </div>
        <button data-testid="fp-auto-sync-toggle" onClick={toggle} disabled={busy || readOnly}
          className={`relative h-7 w-12 shrink-0 rounded-full transition-colors ${s.enabled ? "bg-[#128C3E]" : "bg-[#D1D1D6]"} ${readOnly ? "cursor-not-allowed opacity-60" : ""}`}>
          <span className={`absolute top-0.5 h-6 w-6 rounded-full bg-white shadow transition-all ${s.enabled ? "left-[22px]" : "left-0.5"}`}>
            {busy && <Loader2 className="m-1 h-4 w-4 animate-spin text-[#8E8E93]" />}
          </span>
        </button>
      </div>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Field label="Automatic Sync" value={<span className={s.enabled ? "text-[#0E8A46]" : "text-[#8E8E93]"}>{s.enabled ? "ON" : "OFF"}</span>} />
        <Field label="Effective From" value={fmtDay(s.effective_from)} />
        <Field label="Last Auto Activation" value={s.last_auto_period ? `${s.last_auto_period}` : "—"} />
        <Field label="Next Expected Period" value={<span data-testid="fp-auto-next">{s.next_expected_period}{s.next_will_auto ? " · auto" : ""}</span>} />
      </div>
      {s.last_auto_activation && (
        <p className="text-[11.5px] text-[#8E8E93]">Last automatic activation: {fmtDate(s.last_auto_activation)}</p>
      )}
    </section>
  );
}
